import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowLeft, ShieldCheck, KeyRound, Smartphone } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("creds"); // creds | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function step1(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      setStep("otp"); setHint(data.mobile_hint || "");
      if (data.dev_mode) { setDevOtp(data.dev_otp); setOtp(data.dev_otp); }
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  }

  async function step2(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/admin/verify-otp", { email, otp });
      login(data.token, data.user);
      nav("/admin");
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-forest p-4">
      <div className="w-full max-w-sm rounded-xl border border-lime/30 bg-card p-7 shadow-lg">
        <button onClick={() => nav("/")} className="mb-5 flex items-center gap-1 text-sm text-ink/50 hover:text-forest"><ArrowLeft className="h-4 w-4" /> Home</button>
        <Logo className="mb-4" />
        <div className="mb-6 flex items-center gap-2 text-forest"><ShieldCheck className="h-5 w-5" /><span className="font-heading text-lg font-medium">Secure Admin Access</span></div>

        <div className="mb-5 flex items-center gap-2 text-xs">
          <StepDot n={1} active={step === "creds"} done={step === "otp"} label="Credentials" />
          <div className="h-px flex-1 bg-border" />
          <StepDot n={2} active={step === "otp"} label="Mobile OTP" />
        </div>

        {err && <Alert className="mb-4 border-red-300 bg-red-50"><AlertDescription className="text-red-700" data-testid="admin-error">{err}</AlertDescription></Alert>}

        {step === "creds" ? (
          <form onSubmit={step1} className="space-y-4">
            <div><Label>Email</Label><Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Password</Label><Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" /></div>
            <Button data-testid="admin-step1-btn" type="submit" className="w-full bg-forest text-white hover:bg-forest/90" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
          </form>
        ) : (
          <form onSubmit={step2} className="space-y-4">
            {devOtp && (
              <Alert className="border-lime bg-lime/20"><KeyRound className="h-4 w-4 text-forest" />
                <AlertDescription className="text-forest" data-testid="admin-dev-otp"><b>DEV/TEST MODE</b> — OTP is <b className="num">{devOtp}</b></AlertDescription></Alert>
            )}
            <div className="flex items-center gap-2 text-sm text-ink/60"><Smartphone className="h-4 w-4" /> OTP sent to mobile ending {hint}</div>
            <div><Label>Enter OTP</Label><Input data-testid="admin-otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="num mt-1.5 text-lg tracking-widest" /></div>
            <Button data-testid="admin-step2-btn" type="submit" className="w-full bg-forest text-white hover:bg-forest/90" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Access Dashboard"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function StepDot({ n, active, done, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${active ? "bg-forest text-white" : done ? "bg-lime text-ink" : "bg-psage text-forest"}`}>{n}</span>
      <span className={active ? "text-forest" : "text-ink/50"}>{label}</span>
    </div>
  );
}
